import {useCallback, useEffect, useMemo, useState} from 'react';
import {SessionStorage} from '../storage';

export interface PaginationState {
  page: number;
  pageSize: number;
  totalItems: number;
}

export interface PaginationConfig {
  key: string;
  defaultPageSize?: number;
  defaultPage?: number;
}

export interface UsePaginationReturn {
  page: number;
  pageSize: number;
  totalItems: number;
  pages: number;

  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setTotalItems: (totalItems: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  reset: () => void;

  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startItem: number;
  endItem: number;
  isEmpty: boolean;
}

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE = 1;

export function usePagination(config: PaginationConfig): UsePaginationReturn {
  const {key, defaultPageSize = DEFAULT_PAGE_SIZE, defaultPage = DEFAULT_PAGE} = config;

  const [state, setState] = useState<PaginationState>(() => {
    const stored = SessionStorage.get<Partial<PaginationState>>(`pagination-${key}`);

    if (stored) {
      return {
        page: stored.page || defaultPage,
        pageSize: stored.pageSize || defaultPageSize,
        totalItems: stored.totalItems || 0,
      };
    }

    return {
      page: defaultPage,
      pageSize: defaultPageSize,
      totalItems: 0,
    };
  });

  useEffect(() => {
    SessionStorage.set(`pagination-${key}`, state);
  }, [key, state]);

  const pages = useMemo(() => {
    return state.totalItems > 0 ? Math.ceil(state.totalItems / state.pageSize) : 0;
  }, [state.totalItems, state.pageSize]);

  const hasNextPage = useMemo(() => state.page < pages, [state.page, pages]);
  const hasPreviousPage = useMemo(() => state.page > 1, [state.page]);

  const startItem = useMemo(() => {
    return state.totalItems === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
  }, [state.page, state.pageSize, state.totalItems]);

  const endItem = useMemo(() => {
    return Math.min(state.page * state.pageSize, state.totalItems);
  }, [state.page, state.pageSize, state.totalItems]);

  const isEmpty = useMemo(() => state.totalItems === 0, [state.totalItems]);

  const setPage = useCallback(
    (page: number) => {
      const clampedPage = Math.max(1, Math.min(page, pages || 1));
      setState(prev => ({...prev, page: clampedPage}));
    },
    [pages],
  );

  const setPageSize = useCallback((pageSize: number) => {
    const validPageSize = Math.max(1, pageSize);
    setState(prev => {
      const currentFirstItem = (prev.page - 1) * prev.pageSize + 1;
      const newPage = Math.max(1, Math.ceil(currentFirstItem / validPageSize));

      return {
        ...prev,
        pageSize: validPageSize,
        page: newPage,
      };
    });
  }, []);

  const setTotalItems = useCallback((totalItems: number) => {
    const validTotalItems = Math.max(0, totalItems);
    setState(prev => {
      const newPages =
        validTotalItems > 0 ? Math.ceil(validTotalItems / prev.pageSize) : 0;
      const clampedPage = prev.page > newPages && newPages > 0 ? newPages : prev.page;

      return {
        ...prev,
        totalItems: validTotalItems,
        page: clampedPage,
      };
    });
  }, []);

  const nextPage = useCallback(() => {
    setPage(state.page + 1);
  }, [setPage, state.page]);

  const previousPage = useCallback(() => {
    setPage(state.page - 1);
  }, [setPage, state.page]);

  const goToFirstPage = useCallback(() => {
    setPage(1);
  }, [setPage]);

  const goToLastPage = useCallback(() => {
    setPage(pages);
  }, [setPage, pages]);

  const reset = useCallback(() => {
    setState({
      page: defaultPage,
      pageSize: defaultPageSize,
      totalItems: 0,
    });
  }, [defaultPage, defaultPageSize]);

  return {
    page: state.page,
    pageSize: state.pageSize,
    totalItems: state.totalItems,
    pages,

    setPage,
    setPageSize,
    setTotalItems,
    nextPage,
    previousPage,
    goToFirstPage,
    goToLastPage,
    reset,

    hasNextPage,
    hasPreviousPage,
    startItem,
    endItem,
    isEmpty,
  };
}
